import { useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import axios from 'axios'

import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { URL as BACKEND_URL } from '../../../gql/client'
import { IAllUserNode } from '../../../types/users'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
  fetchVicidialCampaignOptions,
} from '../../channelConfig/hooks/useChannelConfig'
import { DifySection } from '../../channelConfig/components/DifySection'
import { PromptConfigSection } from '../../channelConfig/components/PromptConfigSection'

interface Props {
  node: IAllUserNode
  userForm: any
  handleUserChange: (field: string, value: any) => void
  campaignOptions: VicidialCampaignOption[]
  savingUserConfig: boolean
  isUserDirty: boolean
  userId: string | undefined
  loadingUserConfig: boolean
  handleSaveUserConfig: () => void
  orgConfig: Partial<ChannelConfigData>
  userConfig: Partial<ChannelConfigData>
  showWebhookSecret: boolean
  setShowWebhookSecret: (value: boolean) => void
  handleResetToInherited: () => void
}

type TestChannel = 'whatsapp' | 'sms' | 'email'

export const PersonalSettingsTab: React.FC<Props> = ({
  node,
  userForm,
  handleUserChange,
  campaignOptions,
  savingUserConfig,
  isUserDirty,
  userId,
  loadingUserConfig,
  handleSaveUserConfig,
  orgConfig,
  userConfig,
  showWebhookSecret,
  setShowWebhookSecret,
  handleResetToInherited,
}) => {
  const { auth } = useAuth()
  const [testModal, setTestModal] = useState<TestChannel | null>(null)
  const [testRecipient, setTestRecipient] = useState('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  const openTestModal = (channel: TestChannel) => {
    setTestModal(channel)
    setTestRecipient('')
    setTestResult(null)
  }

  const handleTestConnection = async () => {
    if (!testRecipient.trim()) {
      toast.error('Enter a recipient')
      return
    }
    setTestLoading(true)
    setTestResult(null)
    try {
      const token = getToken()
      const endpoint =
        testModal === 'whatsapp' ? 'test-whatsapp' :
          testModal === 'sms' ? 'test-sms' :
            'test-email'
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/channel-config/${endpoint}`,
        {
          recipient: testRecipient,
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setTestResult({
        ok: res.data?.ok ?? true,
        message: res.data?.message || 'Test sent successfully',
      })
    } catch (error: any) {
      setTestResult({
        ok: false,
        message: error.response?.data?.error || 'Test failed',
      })
    } finally {
      setTestLoading(false)
    }
  }

  return (
    <>
      <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
        <div>
          <p className='text-muted fs-7 mb-0'>
            Overrides exclusive to this agent. These take priority over organization defaults.
          </p>
        </div>
        <Button
          variant='primary'
          onClick={handleSaveUserConfig}
          disabled={savingUserConfig || !isUserDirty || !userId}
        >
          {savingUserConfig ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            <>
              <i className='bi bi-check-lg me-1' />
              Save Personal Settings
            </>
          )}
        </Button>
      </div>

      {!userId ? (
        <Alert variant='warning' className='mb-0'>
          Save the user first before configuring personal settings.
        </Alert>
      ) : loadingUserConfig ? (
        <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 180 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        <div className='row g-3'>
          {/* ─── Personal WhatsApp Number ─── */}
          <div className='col-md-6'>
            <Card className='h-100'>
              <Card.Body className='p-4'>
                <div className='d-flex align-items-center gap-2 mb-3 pb-2 border-bottom'>
                  <i className='bi bi-whatsapp fs-3 text-success' />
                  <h5 className='fw-bold text-gray-800 mb-0'>WhatsApp</h5>
                  <Badge bg={userForm.whatsapp_provider && userForm.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className='ms-auto'>
                    {userForm.whatsapp_provider && userForm.whatsapp_provider !== 'NONE' ? userForm.whatsapp_provider : 'Disabled'}
                  </Badge>
                </div>
                <p className='text-muted fs-7 mb-4'>
                  Assign a direct phone number to this agent. Incoming messages will route exclusively to them.
                </p>
                <div className='row g-3'>
                  <div className='col-12'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>Provider</Form.Label>
                      <Form.Select
                        value={userForm.whatsapp_provider || 'NONE'}
                        onChange={(e) => handleUserChange('whatsapp_provider', e.target.value)}
                        size='sm'
                      >
                        <option value='NONE'>Disabled (inherit organization)</option>
                        <option value='TWILIO'>Twilio</option>
                        <option value='BOTMAKER'>Botmaker</option>
                        <option value='META'>Meta Cloud API</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  {userForm.whatsapp_provider === 'TWILIO' && (
                    <>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>WhatsApp Number</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.twilio_whatsapp_number || ''}
                            onChange={(e) => handleUserChange('twilio_whatsapp_number', e.target.value)}
                            placeholder='+1234567890'
                            size='sm'
                          />
                          <Form.Text className='text-muted'>Requires + prefix</Form.Text>
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Account SID (Optional)</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.twilio_account_sid || ''}
                            onChange={(e) => handleUserChange('twilio_account_sid', e.target.value)}
                            placeholder='AC...'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Auth Token (Optional)</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.twilio_auth_token || ''}
                            onChange={(e) => handleUserChange('twilio_auth_token', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <div className='d-flex align-items-end h-100'>
                          <Button
                            variant='outline-success'
                            size='sm'
                            className='w-100'
                            onClick={() => openTestModal('whatsapp')}
                          >
                            <i className='bi bi-send-check me-2'></i>Test Connection
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  {userForm.whatsapp_provider === 'BOTMAKER' && (
                    <div className='col-12'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>Botmaker Phone Line</Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.botmaker_phone_line_number || ''}
                          onChange={(e) => handleUserChange('botmaker_phone_line_number', e.target.value)}
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                  )}
                  {userForm.whatsapp_provider === 'META' && (
                    <div className='col-12'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>Meta Phone Number ID</Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.meta_whatsapp_phone_number_id || ''}
                          onChange={(e) => handleUserChange('meta_whatsapp_phone_number_id', e.target.value)}
                          placeholder='1234567890'
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                  )}
                  {userConfig?.webhook_token && userForm.whatsapp_provider && userForm.whatsapp_provider !== 'NONE' && (
                    <div className='col-12 mt-2'>
                      <div className='row g-2 align-items-center p-2 bg-light border rounded'>
                        <span className='fs-8 fw-semibold col-auto'>Webhook:</span>
                        <code className='col text-dark fs-7 text-break'>
                          {userForm.whatsapp_provider === 'TWILIO' && `${BACKEND_URL}/webhooks/twilio/whatsapp/${userConfig.webhook_token}/`}
                          {userForm.whatsapp_provider === 'BOTMAKER' && `${BACKEND_URL}/webhooks/botmaker/${userConfig.webhook_token}/`}
                          {userForm.whatsapp_provider === 'META' && `${BACKEND_URL}/webhooks/meta/${userConfig.webhook_token}/`}
                        </code>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary p-1 col-auto'
                          onClick={() => {
                            const url =
                              userForm.whatsapp_provider === 'TWILIO' ? `${BACKEND_URL}/webhooks/twilio/whatsapp/${userConfig.webhook_token}/` :
                              userForm.whatsapp_provider === 'BOTMAKER' ? `${BACKEND_URL}/webhooks/botmaker/${userConfig.webhook_token}/` :
                              `${BACKEND_URL}/webhooks/meta/${userConfig.webhook_token}/`
                            navigator.clipboard.writeText(url)
                            toast.success('Webhook URL copied')
                          }}
                          title='Copy Webhook URL'
                          style={{ minWidth: '28px' }}
                        >
                          <i className='bi bi-clipboard' />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── Personal SMS Override ─── */}
          <div className='col-md-6'>
            <Card className='h-100'>
              <Card.Body className='p-4'>
                <div className='d-flex align-items-center gap-2 mb-3 pb-2 border-bottom'>
                  <i className='bi bi-chat-dots fs-3 text-info' />
                  <h5 className='fw-bold text-gray-800 mb-0'>SMS (Twilio)</h5>
                  <Badge bg={userForm.twilio_enabled ? 'success' : 'secondary'} className='ms-auto'>
                    {userForm.twilio_enabled ? 'Override' : 'Inherit'}
                  </Badge>
                </div>
                <p className='text-muted fs-7 mb-4'>
                  Override organization SMS credentials. Leave disabled to inherit settings.
                </p>
                <div className='row g-3'>
                  <div className='col-12'>
                    <Form.Check
                      type='switch'
                      id='user_twilio_enabled'
                      label='Enable SMS override'
                      checked={userForm.twilio_enabled}
                      onChange={(e) => handleUserChange('twilio_enabled', e.target.checked)}
                      className='fw-semibold'
                    />
                  </div>
                  {userForm.twilio_enabled && (
                    <>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Phone Number</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.twilio_phone_number || ''}
                            onChange={(e) => handleUserChange('twilio_phone_number', e.target.value)}
                            placeholder='+1234567890'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Account SID (Optional)</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.twilio_account_sid || ''}
                            onChange={(e) => handleUserChange('twilio_account_sid', e.target.value)}
                            placeholder='AC...'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Auth Token (Optional)</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.twilio_auth_token || ''}
                            onChange={(e) => handleUserChange('twilio_auth_token', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <div className='d-flex align-items-end h-100'>
                          <Button
                            variant='outline-info'
                            size='sm'
                            className='w-100'
                            onClick={() => openTestModal('sms')}
                          >
                            <i className='bi bi-send-check me-2'></i>Test Connection
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  {userConfig?.webhook_token && userForm.twilio_enabled && (
                    <div className='col-12 mt-2'>
                      <div className='row g-2 align-items-center p-2 bg-light border rounded'>
                        <span className='fs-8 fw-semibold col-auto'>Inbound Webhook:</span>
                        <code className='col text-dark fs-7 text-break'>{`${BACKEND_URL}/webhooks/twilio/sms/${userConfig.webhook_token}/`}</code>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary p-1 col-auto'
                          onClick={() => {
                            navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/sms/${userConfig.webhook_token}/`)
                            toast.success('Webhook URL copied')
                          }}
                          title='Copy Webhook URL'
                          style={{ minWidth: '28px' }}
                        >
                          <i className='bi bi-clipboard' />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── Personal Email SMTP Override ─── */}
          <div className='col-md-6'>
            <Card className='h-100'>
              <Card.Body className='p-4'>
                <div className='d-flex align-items-center gap-2 mb-3 pb-2 border-bottom'>
                  <i className='bi bi-envelope fs-3 text-primary' />
                  <h5 className='fw-bold text-gray-800 mb-0'>Email (SMTP)</h5>
                  <Badge bg={userForm.email_enabled ? 'success' : 'secondary'} className='ms-auto'>
                    {userForm.email_enabled ? 'Override' : 'Inherit'}
                  </Badge>
                </div>
                <p className='text-muted fs-7 mb-4'>
                  Override organization SMTP settings. Leave disabled to inherit settings.
                </p>
                <div className='row g-3'>
                  <div className='col-12'>
                    <Form.Check
                      type='switch'
                      id='user_email_enabled'
                      label='Enable SMTP override'
                      checked={userForm.email_enabled}
                      onChange={(e) => handleUserChange('email_enabled', e.target.checked)}
                      className='fw-semibold'
                    />
                  </div>
                  {userForm.email_enabled && (
                    <>
                      <div className='col-12'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>SMTP Host</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.email_host || ''}
                            onChange={(e) => handleUserChange('email_host', e.target.value)}
                            placeholder='smtp.gmail.com'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Port</Form.Label>
                          <Form.Control
                            type='number'
                            value={userForm.email_port || 587}
                            onChange={(e) =>
                              handleUserChange(
                                'email_port',
                                e.target.value === '' ? 587 : Number(e.target.value)
                              )
                            }
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6 d-flex align-items-end'>
                        <Form.Check
                          type='switch'
                          id='user_email_use_tls'
                          label='Use TLS'
                          checked={userForm.email_use_tls}
                          onChange={(e) => handleUserChange('email_use_tls', e.target.checked)}
                          className='mb-0 fw-semibold fs-8'
                        />
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Username / Email</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.email_host_user || ''}
                            onChange={(e) => handleUserChange('email_host_user', e.target.value)}
                            placeholder='user@gmail.com'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Password</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.email_host_password || ''}
                            onChange={(e) => handleUserChange('email_host_password', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>From Address</Form.Label>
                          <Form.Control
                            type='email'
                            value={userForm.email_from || ''}
                            onChange={(e) => handleUserChange('email_from', e.target.value)}
                            placeholder='Agent <agent@agency.com>'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Reply-To (Optional)</Form.Label>
                          <Form.Control
                            type='email'
                            value={userForm.email_reply_to || ''}
                            onChange={(e) => handleUserChange('email_reply_to', e.target.value)}
                            placeholder='support@agency.com'
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-12'>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          className='w-100'
                          onClick={() => openTestModal('email')}
                        >
                          <i className='bi bi-send-check me-2'></i>Test Connection
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── PolicyDial Settings ─── */}
          <div className='col-md-6'>
            <Card className='h-100'>
              <Card.Body className='p-4'>
                <div className='d-flex align-items-center gap-2 mb-3 pb-2 border-bottom'>
                  <img src='/media/tfi/policydial-icon.svg' alt='PolicyDial' style={{ width: '28px', height: '28px' }} />
                  <h5 className='fw-bold text-gray-800 mb-0'>PolicyDial</h5>
                  <Badge bg={userForm.vicidial_enabled ? 'success' : 'secondary'} className='ms-auto'>
                    {userForm.vicidial_enabled ? 'Override' : 'Inherit'}
                  </Badge>
                </div>
                <p className='text-muted fs-7 mb-4'>
                  Per-agent telephony override. Leave empty to inherit organization configuration.
                </p>
                <div className='row g-3'>
                  <div className='col-12'>
                    <div className='alert alert-light-primary border border-primary border-dashed mb-0'>
                      <div className='fw-bold mb-1 fs-8'>Current mode</div>
                      <div className='text-muted fs-8'>
                        {userForm.telephony_provider
                          ? `Using ${userForm.telephony_provider}`
                          : 'Inherits organization provider'}
                      </div>
                    </div>
                  </div>
                  <div className='col-12'>
                    <Form.Group className='mb-0'>
                      <Form.Label className='fw-semibold text-gray-700 fs-8'>Telephony Provider</Form.Label>
                      <Form.Select
                        value={userForm.telephony_provider || ''}
                        onChange={(e) =>
                          handleUserChange('telephony_provider', e.target.value as TelephonyProviderValue)
                        }
                        size='sm'
                      >
                        <option value=''>Inherit organization</option>
                        <option value='RINGCENTRAL'>RingCentral</option>
                        <option value='VICIDIAL'>VICIdial</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className='col-12'>
                    <Form.Check
                      type='switch'
                      id='user_vicidial_enabled'
                      label='Enable VICIdial override'
                      checked={userForm.vicidial_enabled}
                      onChange={(e) => handleUserChange('vicidial_enabled', e.target.checked)}
                      className='fw-semibold'
                    />
                  </div>
                  {userForm.vicidial_enabled && (
                    <>
                      <div className='col-12'>
                        <h6 className='fw-bold text-info fs-8 mb-2'>Agent Credentials</h6>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Agent User</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.vicidial_agent_user}
                            onChange={(e) => handleUserChange('vicidial_agent_user', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Agent Password</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.vicidial_agent_password}
                            onChange={(e) => handleUserChange('vicidial_agent_password', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Extension / Phone Login</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.vicidial_phone_login}
                            onChange={(e) => handleUserChange('vicidial_phone_login', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Phone Password</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.vicidial_phone_password}
                            onChange={(e) => handleUserChange('vicidial_phone_password', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-12'>
                        <h6 className='fw-bold text-success fs-8 mb-2'>Campaign Routing</h6>
                      </div>
                      <div className='col-12'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Campaign</Form.Label>
                          {campaignOptions.length > 0 ? (
                            <Form.Select
                              value={userForm.vicidial_campaign_id}
                              onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                              size='sm'
                            >
                              <option value=''>
                                Inherit org {orgConfig.vicidial_campaign_id ? `(${orgConfig.vicidial_campaign_id})` : ''}
                              </option>
                              {campaignOptions.map((campaign) => (
                                <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                  {campaign.campaign_id} - {campaign.campaign_name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type='text'
                              placeholder='Inherit organization'
                              value={userForm.vicidial_campaign_id}
                              onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                              size='sm'
                            />
                          )}
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Auto Dial Delay (seconds)</Form.Label>
                          <Form.Control
                            type='number'
                            min={0}
                            step={1}
                            placeholder={
                              orgConfig.vicidial_dial_queue_delay_seconds != null
                                ? String(orgConfig.vicidial_dial_queue_delay_seconds)
                                : '5'
                            }
                            value={userForm.vicidial_dial_queue_delay_seconds ?? ''}
                            onChange={(e) =>
                              handleUserChange(
                                'vicidial_dial_queue_delay_seconds',
                                e.target.value === ''
                                  ? null
                                  : Math.max(0, Number(e.target.value))
                              )
                            }
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-12'>
                        <Button
                          variant='light'
                          size='sm'
                          className='w-100'
                          onClick={handleResetToInherited}
                        >
                          <i className='bi bi-arrow-clockwise me-1'></i>Reset to Inherited
                        </Button>
                      </div>
                    </>
                  )}
                  <div className='col-12'>
                    <hr className='my-1' />
                    <Form.Check
                      type='switch'
                      id='twilio_provisioning_enabled'
                      label='Allow self-provisioning of Caller IDs (My Numbers)'
                      checked={!!userForm.twilio_provisioning_enabled}
                      onChange={(e) => handleUserChange('twilio_provisioning_enabled', e.target.checked)}
                      className='fw-semibold'
                    />
                    <div className='text-muted fs-8 mt-1'>
                      Shows the “My Numbers” tab in this user’s Profile so they can buy their own Twilio Caller IDs (inbound + outbound).
                    </div>
                  </div>
                  {userForm.twilio_provisioning_enabled && (
                    <div className='col-md-6'>
                      <Form.Group className='mb-0'>
                        <Form.Label className='fw-semibold text-gray-700 fs-8'>Max numbers</Form.Label>
                        <Form.Control
                          type='number'
                          min={1}
                          step={1}
                          placeholder='2'
                          value={userForm.twilio_provisioning_max_numbers ?? 2}
                          onChange={(e) =>
                            handleUserChange(
                              'twilio_provisioning_max_numbers',
                              e.target.value === '' ? 1 : Math.max(1, Number(e.target.value))
                            )
                          }
                          size='sm'
                        />
                      </Form.Group>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── HealthSherpa Medicare ─── */}
          <div className='col-md-6'>
            <Card className='h-100'>
              <Card.Body className='p-4'>
                <div className='d-flex align-items-center gap-2 mb-3 pb-2 border-bottom'>
                  <i className='bi bi-heart-pulse fs-3 text-danger' />
                  <h5 className='fw-bold text-gray-800 mb-0'>HealthSherpa</h5>
                  <Badge bg={userForm.healthsherpa_enabled ? 'success' : 'secondary'} className='ms-auto'>
                    {userForm.healthsherpa_enabled ? 'Override' : 'Inherit'}
                  </Badge>
                </div>
                <p className='text-muted fs-7 mb-4'>
                  Override organization HealthSherpa credentials. Leave blank to inherit from organization.
                </p>
                <div className='row g-3'>
                  <div className='col-12'>
                    <Form.Check
                      type='switch'
                      id='user_healthsherpa_enabled'
                      label='Use personal HealthSherpa credentials'
                      checked={userForm.healthsherpa_enabled || false}
                      onChange={(e) => handleUserChange('healthsherpa_enabled', e.target.checked)}
                      className='fw-semibold'
                    />
                  </div>
                  {userForm.healthsherpa_enabled && (
                    <>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Agent Email</Form.Label>
                          <Form.Control
                            type='email'
                            placeholder='agent@example.com'
                            value={userForm.healthsherpa_email || ''}
                            onChange={(e) => handleUserChange('healthsherpa_email', e.target.value)}
                            size='sm'
                          />
                          <Form.Text className='text-muted'>Must match registered agent</Form.Text>
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>API Key</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='off'
                            placeholder='Enter API key'
                            value={userForm.healthsherpa_api_key || ''}
                            onChange={(e) => handleUserChange('healthsherpa_api_key', e.target.value)}
                            size='sm'
                          />
                        </Form.Group>
                      </div>
                      <div className='col-12'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-semibold text-gray-700 fs-8'>Webhook Secret</Form.Label>
                          <div className='d-flex gap-1 align-items-start'>
                            <Form.Control
                              type={showWebhookSecret ? 'text' : 'password'}
                              autoComplete='off'
                              placeholder='Secret'
                              value={userForm.healthsherpa_webhook_secret || ''}
                              onChange={(e) => handleUserChange('healthsherpa_webhook_secret', e.target.value)}
                              size='sm'
                            />
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                              disabled={!userForm.healthsherpa_webhook_secret}
                              className='mt-1'
                              style={{ minWidth: '28px', padding: '0.25rem' }}
                            >
                              <i className={`bi ${showWebhookSecret ? 'bi-eye-slash' : 'bi-eye'} fs-8`}></i>
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => {
                                navigator.clipboard.writeText(userForm.healthsherpa_webhook_secret || '')
                                toast.success('Copied')
                              }}
                              disabled={!userForm.healthsherpa_webhook_secret}
                              className='mt-1'
                              style={{ minWidth: '28px', padding: '0.25rem' }}
                            >
                              <i className='bi bi-clipboard fs-8'></i>
                            </Button>
                          </div>
                        </Form.Group>
                      </div>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* ─── Personal Dify AI Configuration ─── */}
          <div className='col-12 mt-4'>
            <DifySection
              difyEnabled={userForm.dify_enabled || false}
              difyApiKey={userForm.dify_api_key || ''}
              difyApiUrl={userForm.dify_api_url || 'https://api.dify.ai/v1'}
              difyAppId={userForm.dify_app_id || ''}
              onDifyEnabledChange={(value) => handleUserChange('dify_enabled', value)}
              onDifyApiKeyChange={(value) => handleUserChange('dify_api_key', value)}
              onDifyApiUrlChange={(value) => handleUserChange('dify_api_url', value)}
              onDifyAppIdChange={(value) => handleUserChange('dify_app_id', value)}
              organizationId={userId || ''}
              switchLabel='Habilitar IA Conversacional para mis chats'
              aiProvider={userForm.ai_provider || 'DIFY'}
              onAiProviderChange={(value) => handleUserChange('ai_provider', value)}
              llmProvider={userForm.llm_provider || 'ANTHROPIC'}
              onLlmProviderChange={(value) => handleUserChange('llm_provider', value)}
              llmApiKey={userForm.llm_api_key || ''}
              onLlmApiKeyChange={(value) => handleUserChange('llm_api_key', value)}
              llmModel={userForm.llm_model || ''}
              onLlmModelChange={(value) => handleUserChange('llm_model', value)}
            />
            {userForm.dify_enabled && (
              <div className='pt-4 border-top mt-4 ps-4'>
                <PromptConfigSection
                  isOrgScope={false}
                  promptHeader={userForm.prompt_header || ''}
                  onPromptHeaderChange={(value) => handleUserChange('prompt_header', value)}
                  promptBody={userForm.prompt_body || ''}
                  onPromptBodyChange={(value) => handleUserChange('prompt_body', value)}
                  promptFooter={userForm.prompt_footer || ''}
                  onPromptFooterChange={(value) => handleUserChange('prompt_footer', value)}
                  shareContactInfo={userForm.share_contact_info ?? null}
                  onShareContactInfoChange={(value) => handleUserChange('share_contact_info', value)}
                  shareQuotes={userForm.share_quotes ?? null}
                  onShareQuotesChange={(value) => handleUserChange('share_quotes', value)}
                  sharePolicies={userForm.share_policies ?? null}
                  onSharePoliciesChange={(value) => handleUserChange('share_policies', value)}
                  shareLeads={userForm.share_leads ?? null}
                  onShareLeadsChange={(value) => handleUserChange('share_leads', value)}
                  shareConsentStatus={userForm.share_consent_status ?? null}
                  onShareConsentStatusChange={(value) => handleUserChange('share_consent_status', value)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Connection Modal */}
      <Modal show={!!testModal} onHide={() => setTestModal(null)} centered size='sm'>
        <Modal.Header closeButton>
          <Modal.Title className='fs-6'>
            {testModal === 'whatsapp' && <><i className='bi bi-whatsapp text-success me-2'></i>Test WhatsApp</>}
            {testModal === 'sms' && <><i className='bi bi-chat-dots text-info me-2'></i>Test SMS</>}
            {testModal === 'email' && <><i className='bi bi-envelope text-primary me-2'></i>Test Email</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              {testModal === 'email' ? 'Destination email' : 'Destination number'}
            </Form.Label>
            <Form.Control
              type={testModal === 'email' ? 'email' : 'tel'}
              placeholder={testModal === 'email' ? 'you@example.com' : '+1 305 555 0100'}
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTestConnection()}
            />
            {testModal !== 'email' && (
              <Form.Text className='text-muted'>Include country code, e.g. +1 for USA.</Form.Text>
            )}
          </Form.Group>
          {testResult && (
            <div className={`alert mt-3 py-2 small ${testResult.ok ? 'alert-success' : 'alert-danger'}`}>
              <i className={`bi ${testResult.ok ? 'bi-check-circle' : 'bi-x-circle'} me-2`}></i>
              {testResult.message}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className='py-2'>
          <Button variant='outline-secondary' size='sm' onClick={() => setTestModal(null)}>
            Close
          </Button>
          <Button
            variant={testModal === 'whatsapp' ? 'success' : testModal === 'sms' ? 'info' : 'primary'}
            size='sm'
            onClick={handleTestConnection}
            disabled={testLoading || !testRecipient.trim()}
          >
            {testLoading
              ? <><Spinner animation='border' size='sm' className='me-1' />Sending...</>
              : <><i className='bi bi-send me-1'></i>Send Test</>
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
